import { cn } from '@repo/tailwind-config/tailwind.functions'
import { IFile } from '@repo/toolkit/types'
import InnerBox from '@repo/ui/inner-box'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { Fragment } from 'react/jsx-runtime'

import { getTripFolderBookings } from '../../../../api/trip-folder/get-trip-folder-bookings'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { Trip } from '../../../../lib/types/models/trip/trip'
import { TripFolderBooking } from '../../../../lib/types/models/trip-folder/trip-folder'
import { TripFolderTimezone } from '../../../../views/trip-folder/trip-folder-details/folder-details'
import DottedLine from '../../../elements/dotted-line'
import TripFolderReservationDetails from './trip-folder-reservation-details'
import TripFolderReservationHeader from './trip-folder-reservation-header'

interface Props {
  folderId: number
  activeTimezone: TripFolderTimezone
}

interface TripInfo {
  name: string
  picto: IFile | undefined
}

const TripFolderReservationsSection = ({ folderId, activeTimezone }: Props) => {
  const { data: tripFolderBookings, isPending } = useQuery({
    queryKey: ['trip-folder-bookings', folderId],
    queryFn: () => getTripFolderBookings(folderId)
  })

  const secondaryTripBookingAvailable = tripFolderBookings?.some((tfb) => tfb.secondaryReservation)

  const getTripInfo = (
    predicate: (booking: TripFolderBooking) => boolean,
    tripSelector: (booking: TripFolderBooking) => Trip | undefined
  ): TripInfo => {
    const booking = tripFolderBookings?.find(predicate)
    if (!booking) {
      return {
        name: '',
        picto: undefined
      }
    }
    const trip = tripSelector(booking)
    return {
      name: TripProperties.getTripLabel(trip) || '',
      picto: trip?.transportMeans?.picto
    }
  }

  function getMainAndSecondaryTrips(): {
    mainTrip: TripInfo | undefined
    secondaryTrip: TripInfo | undefined
  } {
    let mainTrip: TripInfo | undefined = undefined
    let secondaryTrip: TripInfo | undefined = undefined

    const mainBooking = tripFolderBookings?.find((booking) => Boolean(booking.relatedTrip))
    const secondaryBooking = tripFolderBookings?.find(
      (booking) =>
        Boolean(booking.secondaryReservation) &&
        mainBooking &&
        mainBooking.id === booking.mainReservationId
    )

    if (!mainBooking) return { mainTrip: undefined, secondaryTrip: undefined }

    const trip = mainBooking.relatedTrip
    if (trip) {
      mainTrip = {
        name: TripProperties.getTripLabel(trip) || '',
        picto: trip?.transportMeans?.picto
      }
    }

    if (secondaryBooking) {
      const trip = secondaryBooking.relatedTrip
      if (trip) {
        secondaryTrip = {
          name: TripProperties.getTripLabel(trip) || '',
          picto: trip?.transportMeans?.picto
        }
      }
    }

    return { mainTrip, secondaryTrip }
  }

  const { mainTrip, secondaryTrip } = getMainAndSecondaryTrips()

  if (isPending) return <Skeleton className={'h-[300px] w-full'} />

  if (!isPending && (!tripFolderBookings || tripFolderBookings?.length === 0)) {
    return (
      <div className="flex justify-center items-center p-8 bg-gray-50 rounded-3xl border border-primary-light">
        <Typography variant="secondary-body">Aucune réservation disponible</Typography>
      </div>
    )
  }

  return (
    <InnerBox className="bg-white-hover py-3 gap-4">
      {/* Headers */}
      {tripFolderBookings.length >= 1 && !tripFolderBookings[0].isTakeInCharge && (
        <>
          <div className="flex items-center">
            {mainTrip && (
              <TripFolderReservationHeader
                tripName={mainTrip.name}
                type="main"
                picto={mainTrip.picto}
                showLabel={Boolean(secondaryTrip)}
              />
            )}

            {secondaryTrip && (
              <TripFolderReservationHeader
                tripName={secondaryTrip.name}
                type="secondary"
                picto={secondaryTrip.picto}
                showLabel={secondaryTripBookingAvailable}
              />
            )}
          </div>

          <DottedLine />
        </>
      )}

      {/* Trip Bookings */}
      {tripFolderBookings?.map((tripFolderBooking, index) => {
        const mainTripBooking = tripFolderBooking
        const secondaryTripBooking = tripFolderBooking.secondaryReservation
        return (
          <Fragment key={index}>
            <div className="flex gap-2 flex-col">
              <div
                className={cn('grid', {
                  'grid-cols-2': secondaryTripBookingAvailable,
                  'grid-cols-1': !secondaryTripBookingAvailable
                })}
              >
                {index !== 0 && !tripFolderBooking.isTakeInCharge && (
                  <>
                    <TripFolderReservationHeader
                      tripName={
                        getTripInfo(
                          () => Boolean(mainTripBooking.relatedTrip),
                          () => mainTripBooking.relatedTrip
                        ).name
                      }
                      type="main"
                      picto={mainTripBooking.relatedTrip?.transportMeans?.picto}
                      showLabel={Boolean(mainTripBooking.secondaryReservation)}
                    />

                    {secondaryTripBooking && (
                      <TripFolderReservationHeader
                        tripName={
                          getTripInfo(
                            () => Boolean(secondaryTripBooking.relatedTrip),
                            () => secondaryTripBooking.relatedTrip
                          ).name
                        }
                        type="secondary"
                        picto={secondaryTripBooking.relatedTrip?.transportMeans?.picto}
                      />
                    )}
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 mobile:grid-cols-1 gap-3">
                <TripFolderReservationDetails
                  tripFolderBooking={tripFolderBooking}
                  activeTimezone={activeTimezone}
                />

                {/* If secondary trip exists display this */}
                {tripFolderBooking.secondaryReservation ? (
                  <TripFolderReservationDetails
                    tripFolderBooking={tripFolderBooking.secondaryReservation}
                    activeTimezone={activeTimezone}
                  />
                ) : (
                  secondaryTripBookingAvailable && <div className="w-full" />
                )}
              </div>
            </div>

            {index !== tripFolderBookings.length - 1 && <DottedLine />}
          </Fragment>
        )
      })}
    </InnerBox>
  )
}

export default TripFolderReservationsSection
